import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { Check, Package, ShoppingBag, X } from "lucide-react";
import Swal from "sweetalert2";

const c = {
  greenDk: "#1C4A2E",
  greenMd: "#2D6B45",
  greenLt: "#4A9B6F",
  gold: "#E8A020",
  ink: "#0F1F17",
  muted: "#6B7D74",
  surface: "#F4F6F5",
  border: "rgba(28,74,46,0.1)",
  white: "#FFFFFF",
  surface2: "#E8EDEA",
};

interface Premio {
  premio_id: number;
  nombre: string;
  puntos?: number;
  imagen?: string;
  descripcion?: string;
  estado: string;
}

function formatPts(n: number) {
  return n.toLocaleString("es-CR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function emojiForName(name: string) {
  const s = name.trim();
  if (/tablet|celular|phone|tech|laptop|usb|bluetooth/i.test(s)) return "📱";
  if (/super|vale|mercado|voucher|restaurant|cine/i.test(s)) return "🛒";
  if (/coffee|café|taza|licuadora|micro|olla|plancha|electro/i.test(s)) return "☕";
  return "🎁";
}

interface Props {
  premioView: boolean;
  setPremioView: React.Dispatch<React.SetStateAction<boolean>>;
  premioViewID: number;
  onCartChanged?: () => void;
  inCart?: boolean;
  categoryLabel: (name: string) => string;
}

const PremioView = ({
  premioView,
  setPremioView,
  premioViewID,
  onCartChanged,
  inCart = false,
  categoryLabel,
}: Props) => {
  const [premio, setPremio] = useState<Premio | null>(null);
  const [url, setUrl] = useState<string | undefined>(undefined);
  const [isClosing, setIsClosing] = useState(false);
  const [adding, setAdding] = useState(false);

  const getPremio = useCallback(
    async (id: number) => {
      if (!id) return;
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/premios/${id}`,
          {
            headers: {
              "auth-token": `${sessionStorage?.getItem("token")}`,
            },
          }
        );
        const p = response.data as Premio;
        setPremio(p);
        if (p.imagen) {
          try {
            const { data: s3Data } = await axios.get(
              `${process.env.REACT_APP_API_URL}/api/s3/signed-url?key=${encodeURIComponent(p.imagen)}`,
              { headers: { "auth-token": sessionStorage.getItem("token") } }
            );
            setUrl(s3Data.url);
          } catch {
            setUrl(undefined);
          }
        } else {
          setUrl(undefined);
        }
      } catch {
        setPremio(null);
      }
    },
    []
  );

  useEffect(() => {
    if (premioView === false) {
      const t = setTimeout(() => {
        setUrl(undefined);
        setPremio(null);
      }, 320);
      return () => clearTimeout(t);
    }
    if (premioViewID) {
      getPremio(premioViewID);
    }
  }, [premioView, premioViewID, getPremio]);

  const close = () => {
    if (premioView) {
      setIsClosing(true);
      setTimeout(() => {
        setPremioView(false);
        setIsClosing(false);
      }, 280);
    }
  };

  const addPremioCart = async () => {
    if (!premio?.premio_id) return;
    setAdding(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/carrito/${sessionStorage?.getItem(
          "user_id"
        )}/${premio.premio_id}`,
        {},
        {
          headers: {
            "auth-token": `${sessionStorage?.getItem("token")}`,
          },
        }
      );
      onCartChanged?.();
      Swal.fire({
        icon: "success",
        title: "Premio agregado al carrito",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch {
      Swal.fire({
        icon: "error",
        title: "Error al agregar al carrito",
        text: "Por favor intente de nuevo",
      });
    } finally {
      setAdding(false);
    }
  };

  if (!premioView && !isClosing) {
    return null;
  }

  return (
    <>
      <div
        role="presentation"
        className={`fixed inset-0 z-[940] bg-black/50 transition-opacity duration-200 ${
          isClosing ? "opacity-0" : "opacity-100"
        }`}
        onClick={close}
        onKeyDown={(e) => e.key === "Escape" && close()}
        style={{ maxWidth: "100vw" }}
      />
      <div
        className="fixed bottom-0 left-1/2 z-[950] w-full max-w-[390px] overflow-hidden rounded-t-3xl bg-white shadow-2xl"
        style={{
          boxShadow: "0 -8px 40px rgba(0,0,0,0.2)",
          transform: isClosing
            ? "translate(-50%, 100%)"
            : "translate(-50%, 0)",
          transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div
          className="mx-auto mt-3 h-1 w-9 rounded-sm"
          style={{ background: "rgba(0,0,0,0.12)" }}
        />
        <div
          className="flex h-[180px] items-center justify-center"
          style={{ background: c.surface }}
        >
          {url ? (
            <img
              src={url}
              alt={premio?.nombre ?? "Premio"}
              className="h-full w-full object-contain p-4"
            />
          ) : (
            <span className="text-7xl leading-none" aria-hidden>
              {premio ? emojiForName(premio.nombre) : "🎁"}
            </span>
          )}
        </div>
        <div className="px-5 pb-6 pt-2">
          <p
            className="mb-1 text-[0.7rem] font-semibold uppercase tracking-wider"
            style={{ color: c.greenMd }}
          >
            {premio ? categoryLabel(premio.nombre) : "…"}
          </p>
          <h2
            className="mb-2 text-xl font-extrabold leading-tight"
            style={{ color: c.ink }}
          >
            {premio?.nombre ?? "…"}
          </h2>
          <p
            className="mb-4 text-sm leading-relaxed"
            style={{ color: c.muted }}
          >
            {premio?.descripcion?.trim() || "Sin descripción adicional."}
          </p>
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <div
              className="flex items-center gap-1.5 text-2xl font-extrabold"
              style={{ color: c.gold }}
            >
              <span
                className="flex h-[26px] w-[26px] items-center justify-center rounded-full text-[0.78rem] text-white"
                style={{ background: c.gold }}
                aria-hidden
              >
                ¢
              </span>
              {premio?.puntos != null ? formatPts(premio.puntos) : "—"}
            </div>
            <span
              className="ml-auto inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium"
              style={{ background: c.surface2, color: c.muted }}
            >
              <Package className="h-3.5 w-3.5" />
              {premio?.estado === "Activo" || !premio?.estado
                ? "Disponible"
                : premio.estado}
            </span>
          </div>
          <button
            type="button"
            disabled={adding || inCart}
            onClick={inCart ? undefined : addPremioCart}
            className="flex w-full items-center justify-center gap-2 rounded-2xl py-3.5 text-base font-bold text-white transition disabled:cursor-not-allowed disabled:opacity-60"
            style={{
              background: inCart ? c.greenLt : c.greenMd,
            }}
          >
            {inCart ? (
              <>
                <Check className="h-5 w-5" />
                En tu carrito
              </>
            ) : (
              <>
                <ShoppingBag className="h-[18px] w-[18px]" />
                Agregar al carrito
              </>
            )}
          </button>
          <button
            type="button"
            onClick={close}
            className="mt-2 w-full rounded-xl py-2.5 text-sm font-semibold"
            style={{ color: c.muted }}
          >
            Cancelar
          </button>
        </div>
        <button
          type="button"
          onClick={close}
          className="absolute right-3 top-3 rounded-lg p-1.5"
          style={{ color: c.muted }}
          aria-label="Cerrar"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    </>
  );
};

export default PremioView;
